import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

type ChangeFreq = 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';

export type SeoIndexPolicy = 'index,follow' | 'noindex,nofollow';

export interface SeoPageConfig {
  title: string;
  description: string;
  ogImage: string; // absolute URL
  changefreq?: ChangeFreq;
  priority?: number;
}

const PROD_ORIGIN = 'https://geoastra.com';
const PROD_HOSTNAME = 'geoastra.com';

// Public pages allowlist: only these routes may be indexed (on production hostname only).
const PUBLIC_PAGES: Record<string, SeoPageConfig> = {
  '/': {
    title: 'GeoAstra NTRIP Caster — Cloud NTRIP Hub for RTK Base & Rovers',
    description:
      'GeoAstra NTRIP Caster connects your RTK base(s) and rovers via the internet, turning your base into your own CORS service for survey GNSS, robots, drones, and agriculture.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_NTRIP.png`,
    changefreq: 'daily',
    priority: 0.9
  },
  '/landing-ntrip-caster': {
    title: 'GeoAstra NTRIP Caster 2.0 — Overview & How It Works',
    description:
      'Learn how GeoAstra NTRIP Caster works, why it’s reliable worldwide, and how to connect your RTK base and rovers to the same mountpoint.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_NTRIP.png`,
    changefreq: 'weekly',
    priority: 0.8
  },
  '/landing-quick-start': {
    title: 'Quick Start — GeoAstra NTRIP Caster',
    description:
      'Step-by-step quick start guide to connect your RTK base and rover to GeoAstra NTRIP Caster and start receiving corrections.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_Ntrip_caster_data_flow_diagram.png`,
    changefreq: 'monthly',
    priority: 0.6
  },
  '/landing-whats-new': {
    title: 'What’s New — GeoAstra NTRIP Caster',
    description:
      'Product updates and new capabilities for GeoAstra NTRIP Caster, including live connection monitoring and improved workflows.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/dashboard_screen.png`,
    changefreq: 'monthly',
    priority: 0.6
  },
  '/landing-ppk-downloader': {
    title: 'PPK Downloader — Download RTCM for PPK Workflows',
    description:
      'Download RTCM / RTK base correction data from mountpoints for post-processing (PPK) workflows, with scheduling and status tracking.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/ppk-downloader_screen.png`,
    changefreq: 'monthly',
    priority: 0.5
  },
  '/landing-rtk-base-analyzer': {
    title: 'RTK Base Analyzer — Analyze RTCM Data Streams',
    description:
      'Analyze mountpoint RTCM streams to understand base station status and key metrics for troubleshooting and verification.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/analyzer_screen.png`,
    changefreq: 'monthly',
    priority: 0.5
  },
  '/geoastra-software': {
    title: 'GeoAstra Software — NTRIP Caster, RTK Expert & BLE-GNSS',
    description:
      'Explore GeoAstra NTRIP Caster, RTK Expert, and the BLE-GNSS iOS app for centimeter-level RTK positioning and professional GNSS workflows.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_BLE_GNSS_iOS_3D.jpg`,
    changefreq: 'monthly',
    priority: 0.7
  },
  '/geoastra-gnss-receivers': {
    title: 'GeoAstra GNSS Hardware — RTK, PPP & USB-C Receivers',
    description:
      'Explore GeoAstra GNSS hardware for RTK, PPP, heading, and professional field positioning, including Y55G and GC-9140KB.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_Y55G.png`,
    changefreq: 'monthly',
    priority: 0.7
  },
  '/about-us': {
    title: 'About GeoAstra',
    description:
      'GeoAstra builds RTK software and RTK GNSS receivers to make centimeter-level positioning more affordable and accessible across industries.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.3
  },
  '/contact-us': {
    title: 'Contact GeoAstra',
    description:
      'Contact GeoAstra for sales, partnership, and technical inquiries about our RTK/PPK software and hardware.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/profile.png`,
    changefreq: 'yearly',
    priority: 0.3
  },
  '/refund-policy': {
    title: 'Refund Policy — GeoAstra',
    description: 'GeoAstra refund policy for software and services.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.2
  },
  '/terms-conditions': {
    title: 'Terms & Conditions — GeoAstra',
    description: 'Terms and conditions for using GeoAstra websites, software, and services.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.2
  },
  '/security-privacy': {
    title: 'Security & Privacy — GeoAstra',
    description: 'Security and privacy information for GeoAstra services.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.2
  },
  '/data-processing': {
    title: 'Data Processing — GeoAstra',
    description: 'How GeoAstra processes and protects your data across our services.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.2
  },
  '/disclaimer': {
    title: 'Disclaimer — GeoAstra',
    description: 'Disclaimer for GeoAstra websites, software, and services.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.2
  },
  '/copyright': {
    title: 'Copyright — GeoAstra',
    description: 'Copyright information for GeoAstra content and materials.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.2
  }
};

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /** Normalize router URL to a canonical path (no query/hash, no trailing slash except '/'). */
  normalizePath(url: string): string {
    const raw = (url || '/').split('?')[0].split('#')[0] || '/';
    const withSlash = raw.startsWith('/') ? raw : `/${raw}`;
    if (withSlash !== '/' && withSlash.endsWith('/')) return withSlash.slice(0, -1);
    return withSlash;
  }

  /** On production hostname only, allow indexing for allowlisted public pages. Otherwise noindex everywhere. */
  computeRobotsPolicy(path: string): SeoIndexPolicy {
    const hostname = (typeof window !== 'undefined' && window.location?.hostname) || '';
    const isProdHost = hostname === PROD_HOSTNAME;
    const isPublic = !!PUBLIC_PAGES[path];
    return isProdHost && isPublic ? 'index,follow' : 'noindex,nofollow';
  }

  canonicalUrlForPath(path: string): string {
    if (path === '/') return `${PROD_ORIGIN}/`;
    return `${PROD_ORIGIN}${path}`;
  }

  applyForUrl(url: string): void {
    const path = this.normalizePath(url);
    const page = PUBLIC_PAGES[path];
    const robots = this.computeRobotsPolicy(path);

    const canonical = this.canonicalUrlForPath(path);
    const title = page?.title || 'GeoAstra';
    const description = page?.description || 'GeoAstra';
    const ogImage = page?.ogImage || `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`;

    this.title.setTitle(title);

    // Core meta
    this.meta.updateTag({ name: 'description', content: description }, 'name="description"');
    this.meta.updateTag({ name: 'robots', content: robots }, 'name="robots"');

    // OpenGraph
    this.meta.updateTag({ property: 'og:title', content: title }, 'property="og:title"');
    this.meta.updateTag({ property: 'og:description', content: description }, 'property="og:description"');
    this.meta.updateTag({ property: 'og:url', content: canonical }, 'property="og:url"');
    this.meta.updateTag({ property: 'og:type', content: 'website' }, 'property="og:type"');
    this.meta.updateTag({ property: 'og:image', content: ogImage }, 'property="og:image"');

    // Twitter
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' }, 'name="twitter:card"');
    this.meta.updateTag({ name: 'twitter:title', content: title }, 'name="twitter:title"');
    this.meta.updateTag({ name: 'twitter:description', content: description }, 'name="twitter:description"');
    this.meta.updateTag({ name: 'twitter:image', content: ogImage }, 'name="twitter:image"');

    // Canonical link
    this.upsertCanonicalLink(canonical);

    // Structured data (JSON-LD) - does not change UI content.
    this.upsertOrganizationJsonLd();
    this.upsertProductJsonLdForPath(path, canonical);
  }

  private upsertCanonicalLink(href: string): void {
    const head = this.document.head;
    let link = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  private upsertJsonLdScript(id: string, data: unknown): void {
    const head = this.document.head;
    let script = head.querySelector<HTMLScriptElement>(`script#${id}`);
    if (!script) {
      script = this.document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      head.appendChild(script);
    }
    script.text = JSON.stringify(data);
  }

  private upsertOrganizationJsonLd(): void {
    const org = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'GeoAstra',
      url: PROD_ORIGIN,
      logo: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
      email: 'info@geoastra.com',
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'info@geoastra.com'
        }
      ]
    };
    this.upsertJsonLdScript('ld-org', org);
  }

  /**
   * Optional Product JSON-LD: only added on specific public pages to avoid over-annotating the whole site.
   * This does not change UI content.
   */
  private upsertProductJsonLdForPath(path: string, canonical: string): void {
    if (path === '/geoastra-software') {
      const data = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'GeoAstra Software',
        url: canonical,
        about: [
          { '@type': 'SoftwareApplication', name: 'GeoAstra NTRIP Caster 2.0' },
          { '@type': 'SoftwareApplication', name: 'GeoAstra RTK Expert 1.0' },
          {
            '@type': 'SoftwareApplication',
            name: 'GeoAstra BLE-GNSS',
            operatingSystem: 'iOS, iPadOS',
            applicationCategory: 'UtilitiesApplication'
          }
        ]
      };
      this.upsertJsonLdScript('ld-product', data);
      return;
    }
    if (path === '/geoastra-gnss-receivers') {
      const data = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'GeoAstra GNSS Hardware',
        url: canonical,
        about: [
          { '@type': 'Product', name: 'GeoAstra X8-M1 RTK GNSS Receiver' },
          { '@type': 'Product', name: 'GeoAstra RTU609BT Dual-RTK Heading GNSS Receiver' },
          { '@type': 'Product', name: 'GeoAstra RTU608BT RTK GNSS Receiver' },
          { '@type': 'Product', name: 'GeoAstra Y55G RTK/PPP GNSS Android Device' },
          { '@type': 'Product', name: 'GeoAstra GC-9140KB USB-C GNSS Receiver' }
        ]
      };
      this.upsertJsonLdScript('ld-product', data);
      return;
    }

    // Remove product script on other routes to keep signals clean.
    const existing = this.document.head.querySelector<HTMLScriptElement>('script#ld-product');
    if (existing) existing.remove();
  }
}


